import { locale, t } from "./i18n";
// shared renderer helpers

export function debounce(action: () => void, delayMs: number): () => void {
  let timer = 0;
  return () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(action, delayMs);
  };
}

export function formatDate(isoDate: string): string {
  if (!isoDate) return "";
  return new Date(isoDate).toLocaleDateString(locale(), { day: "numeric", month: "short" });
}

export function formatTime(isoDate: string): string {
  if (!isoDate) return t("never");
  return new Date(isoDate).toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" });
}

export function matchesSearch(search: string, ...fields: string[]): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return fields.some((field) => field.toLowerCase().includes(needle));
}

export function formatBytes(bytes: number): string {
  if (!bytes) return "";
  const units = ["b", "kb", "mb", "gb", "tb"];
  const power = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** power).toFixed(power >= 3 ? 1 : 0)} ${units[power]}`;
}

// systemd timestamps like "Tue 2026-09-29 12:13:53 UTC" turned into "2h 5m"
export function timeSince(timestamp: string): string {
  const started = Date.parse(timestamp.replace(/^\w{3} /, "").replace(" UTC", "Z").replace(" ", "T"));
  if (!started) return "";
  const minutes = Math.floor((Date.now() - started) / 60000);
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  return `${Math.floor(minutes / 1440)}d ${Math.floor((minutes % 1440) / 60)}h`;
}

// escapes html first so only our own bold and code tags ever reach the page
export function formatChatText(text: string): string {
  const escaped = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return escaped
    .replace(/\[\[(?:ref|oc):[a-z0-9]+\]\]/gi, "")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function extractRefIds(text: string, known: Set<string>): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  const add = (raw: string) => {
    const id = raw.toLowerCase();
    if (seen.has(id) || (known.size > 0 && !known.has(id))) return;
    seen.add(id);
    ids.push(id);
  };
  for (const match of text.matchAll(/\[\[ref:([a-z0-9]+)\]\]/gi)) add(match[1]);
  if (!ids.length) {
    for (const match of text.matchAll(/`([a-z0-9]{6,24})`/gi)) add(match[1]);
  }
  return ids.slice(0, 4);
}

export function extractOcIds(text: string): string[] {
  const ids = [...text.matchAll(/\[\[oc:([a-z0-9]+)\]\]/gi)].map((match) => match[1]);
  return [...new Set(ids)].slice(0, 3);
}

export function shrinkImage(file: File, maxPx: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      const scale = Math.min(1, maxPx / Math.max(image.width, image.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error(t("could not shrink that picture")));
        return;
      }
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.72));
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(t("could not read that picture")));
    };
    image.src = url;
  });
}

export function firstLine(text: string, maxLength = 90): string {
  const line = text.split("\n").find((part) => part.trim() !== "") ?? "";
  return line.length > maxLength ? line.slice(0, maxLength - 3) + "..." : line;
}
